import { useLocation, useNavigate } from "react-router-dom";
import { Calendar, Clock, ArrowLeft } from "lucide-react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import Config from "../config.ts";
import Swal from "sweetalert2";
import { Helmet } from "react-helmet-async";
import { fromZonedTime } from "date-fns-tz";
import { format } from "date-fns";

type FormData = {
  owner: string;
  email?: string;
  mobilePhone: string;
  licensePlate: string;
  rideSharingVehicle?: boolean;
  smsAllowed?: boolean;
  emailAllowed?: boolean;
  productionYear?: number;
};

type FormField = {
  name: keyof FormData;
  label: string;
  error: string;
  required?: boolean;
  type?: string;
};

const personFormFields: FormField[] = [
  {
    name: "owner",
    label: "Nume",
    error: "Numele este obligatoriu.",
    required: true,
  },
  {
    name: "mobilePhone",
    label: "Număr telefon",
    type: "tel",
    error: "Numărul de telefon este obligatoriu.",
    required: true,
  },
  {
    name: "email",
    label: "Email (optional)",
    type: "email",
    error: "Email-ul este invalid.",
    required: false,
  },
];

const carFormFields: FormField[] = [
  {
    name: "licensePlate",
    label: "Număr înmatriculare",
    error: "Numărul de înmatriculare este obligatoriu.",
    required: true,
  },
  {
    name: "productionYear",
    label: "Anul fabricatiei",
    error: "Anul fabricatiei este obligatoriu.",
    required: false,
    type: "number",
  },
];

const reservationSchema = yup.object().shape({
  owner: yup.string().required("Numele este obligatoriu."),
  email: yup.string().email("Email invalid").optional(),
  mobilePhone: yup
    .string()
    .matches(/^07\d{8}$/, "Numărul trebuie să conțină doar cifre")
    .required("Numărul de telefon este obligatoriu."),
  licensePlate: yup
    .string()
    .required("Numărul de înmatriculare este obligatoriu."),
  rideSharingVehicle: yup.boolean().optional(),
  productionYear: yup.number(),
  smsAllowed: yup.boolean().optional(),
  emailAllowed: yup.boolean().optional(),
});

const Reservation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { date, time, previousPage } = location.state || {};

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(reservationSchema),
  });

  const onSubmit = async (data: FormData) => {
    const utcDate = fromZonedTime(`${date} ${time}`, Config.TIME_ZONE);

    // Show loading state
    Swal.fire({
      title: "Creare rezervare în curs...",
      text: "Vă rugăm să așteptați",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await axios.post("/api/reservations", {
        owner: data.owner,
        email: data.email,
        mobilePhone: data.mobilePhone,
        licensePlate: data.licensePlate,
        rideSharingVehicle: data.rideSharingVehicle ?? false,
        reservationDate: utcDate.toISOString(),
        smsAllowed: data.smsAllowed,
        emailAllowed: data.emailAllowed,
        productionYear: data.productionYear,
      });

      // Close loading state
      Swal.close();

      if (response.status !== 201) {
        Swal.fire({
          icon: "error",
          title: "Eroare",
          text: "A apărut o eroare la crearea rezervării.",
          confirmButtonText: "OK",
        });
        return;
      }

      Swal.fire({
        title: "Rezervare creată",
        customClass: {
          confirmButton: "custom-ok-button", // Apply custom class to the OK button
        },
        html: `<div style="text-align: left;">
                  <p>Vă rugăm să aveți la dumneavoastră:</p>
                  <p>- Cartea de identitate a vehiculului (CIV)</p>
                  <p>- Talonul</p>
                  <p>- Asigurare valabilă</p>
                  <p>- Actul de identitate al proprietarului</p>
              </div>`,
      }).then(() => {
        navigate(previousPage ?? "/");
      });
    } catch {
      // Close loading state
      Swal.close();

      Swal.fire({
        icon: "error",
        title: "Eroare",
        text: "A apărut o eroare la crearea rezervării. Vă rugăm să încercați din nou.",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>
          Rezervă ITP Online | GabyLen Autoservice & ITP Florești Cluj
        </title>
        <meta
          name="description"
          content="Programează-ți ITP-ul rapid la GabyLen Autoservice Florești. Completează formularul pentru a rezerva data și ora dorită pentru inspecția tehnică periodică."
        />
        <meta
          name="keywords"
          content="rezervare ITP Florești, programare ITP online, rezervă ITP Florești, inspecție tehnică periodică online, ITP Florești Cluj, rezervare ITP GabyLen"
        />
      </Helmet>
      <div className="bg-gray-50 py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 mb-8 hover:text-gray-900"
          >
            <ArrowLeft className="mr-2" size={20} />
            Înapoi la calendar
          </button>
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">
              Completați rezervarea
            </h1>
            <div className="bg-gray-50 p-4 rounded-lg mb-8">
              <div className="flex items-center space-x-6">
                <div className="flex items-center">
                  <Calendar className="text-red-600 mr-2" size={20} />
                  <span>{format(date, "dd/MM/yyyy")}</span>
                </div>
                <div className="flex items-center">
                  <Clock className="text-red-600 mr-2" size={20} />
                  <span>{time}</span>
                </div>
              </div>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4">Date de contact</h2>
                <div className="flex flex-col gap-4">
                  {personFormFields.map(
                    ({ name, label, required, type = "text" }) => (
                      <div key={name}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {label}
                          {required ? (
                            <span className="text-red-600">*</span>
                          ) : null}
                        </label>
                        <input
                          type={type}
                          {...register(name)}
                          className={`w-full p-2 border rounded-md 
                                                focus:outline-none focus:ring-2 focus:ring-gray-200 
                                                ${errors[name] ? "border-red-500" : "border-gray-300"}`}
                        />
                        {errors[name] && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors[name]?.message}
                          </p>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
              <div>
                <h2 className="text-xl font-semibold mb-4">Datele masinii</h2>
                <div className="flex flex-col gap-4">
                  {carFormFields.map(
                    ({ name, label, type = "text", required }) => (
                      <div key={name}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {label}
                          {required ? (
                            <span className="text-red-600">*</span>
                          ) : null}
                        </label>
                        <input
                          type={type}
                          {...register(name)}
                          className={`w-full p-2 border rounded-md 
                                                focus:outline-none focus:ring-2 focus:ring-gray-200 
                                                ${errors[name] ? "border-red-500" : "border-gray-300"}`}
                        />
                        {errors[name] && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors[name]?.message}
                          </p>
                        )}
                      </div>
                    )
                  )}
                  <div>
                    <div className="flex items-center gap-2 font-medium text-gray-700">
                      <label>
                        Masina este taxi sau vehicul de ride sharing?
                      </label>
                      <input
                        className="w-4 h-4"
                        type="checkbox"
                        {...register("rideSharingVehicle")}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h2 className="text-xl font-semibold mb-4">
                  Optiuni suplimentare
                </h2>
                <div className="flex items-center gap-2 font-medium text-gray-700">
                  <label>
                    Doriți să primiți notificări prin SMS despre rezervare?
                  </label>
                  <input
                    className="w-4 h-4"
                    defaultChecked={true}
                    type="checkbox"
                    {...register("smsAllowed")}
                  />
                </div>
                <div className="flex items-center gap-2 font-medium text-gray-700">
                  <label>
                    Doriți să primiți notificări prin email despre rezervare?
                  </label>
                  <input
                    className="w-4 h-4"
                    defaultChecked={true}
                    type="checkbox"
                    {...register("emailAllowed")}
                  />
                </div>
              </div>
              <button
                type="submit"
                className="w-full bg-red-600 text-white px-6 py-3 rounded-md text-lg hover:bg-red-700"
              >
                Rezervă acum
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Reservation;
